import {PerspectiveWorker, Table, View} from '@finos/perspective';
import perspective from '@finos/perspective/dist/esm/perspective.inline.js';
import Debug from 'debug';
import {IObservable, map, ReplaySubject} from "../observable/ReplaySubject";
import {blobToArrayBuffer} from "../utils";
const log = Debug('pdq:services:PdqDataBroker');

export interface TableMeta {
  id: string,
  table: Table,
  options: any,
  references: Set<string>
  parents: Set<string>
  dependents: Set<string>
}
export interface ViewMeta {
  id: string,
  table: Table|undefined,
  view: ReplaySubject<View>,
  options: any,
  references: Set<string>
  parents: Set<string>
  dependents: Set<string>
}

export class PdqDataBroker {
  private _worker: PerspectiveWorker;
  private _tables = new Map<string, TableMeta>();
  private _views = new Map<string, ViewMeta>();
  private DEFAULT_VIEW_CONFIG:any = {};

  dataChanged = new ReplaySubject<Array<TableMeta|ViewMeta>>(1);

  constructor() {
    this._worker = perspective.shared_worker();
  }

  async updateDataSource(id: string, data: any, index: string|undefined = undefined, limit: number|undefined = undefined){
    log(`updateDataSource: ${id} ${data ? 'with data': ''} ${index} ${limit}`)

    data = await this.convertData(data);
    if (!this._tables.has(id)){
      log(`updateDataSource: creating new data source ${id}`)
      const options = {index, limit};
      const table = await this._worker.table(data, options);
      this._setTable(id, {
        id,
          table,
          options,
          references: new Set([]),
          parents: new Set([]),
          dependents: new Set([])
      });
      log(`updateDataSource: created new data source ${id}`)
    } else {
      log(`updateDataSource: updating data source ${id}`)
      const tableMeta = this._tables.get(id)!;
      if (!tableMeta.options?.index) {
        log(`updateDataSource: since no index is defined data on table ${id} the data will be cleared before update`)
        await tableMeta.table.clear();
      }

      tableMeta.table.update(data);
      log(`updateDataSource: updated data source ${id}`)
    }
    log(`updateDataSource: updated`)
  }
  async convertData(data: any){
    if (data instanceof Blob) {
     return await blobToArrayBuffer(data);
    } else {
      return data;
    }
}
  async getTable(table: string): Promise<Table> {
    if (!this._tables.has(table)){
      log(`getTable: Warn: Table ${table} not found... tables: ${[...this._tables.keys()].join(',')}. Will await for it to be created...`)
      const t = await this._getTable(table)
      log(`getTable: Table ${table} is now available.`)
      return t.table;
    }
    log(`getTable: Table ${table} found.`)
    return this._tables.get(table)!.table;
  }
  getOrCreateView = async (id: string, table?: string|undefined, config?: any) => {
    log(`getOrCreateView: ${id} ${table} ${config}`)
    config = config || this.DEFAULT_VIEW_CONFIG;
    if (!table){
      if (this._views.has(id)){
        log(`getOrCreateView: view "${id}" requested, but the table "${this._views.get(id)!.table || '<not defined yet>'}" is still not registered, returning the subject but it is not yet active.`)
        const v = this._views.get(id);
        return v!.view
      }
      log(`getOrCreateView: view "${id}" requested but has not been registered yet, returning the subject but it is not yet active.`)
      const view$ = new ReplaySubject<View>(1);
      this._views.set(id, {
        id,
        table: undefined,
        view: view$,
        options: config,
        references: new Set(),
        parents: new Set(),
        dependents: new Set()
      });
      this._dataChangedHook()
      return view$;
    }
    if (!this._tables.has(table)){
      log(`getOrCreateView: Warn: Table ${table} not found... tables: ${[...this._tables.keys()].join(',')}. Will await for it to be created...`)
      await this._getTable(table)
      log(`getOrCreateView: Table ${table} is now available.`)
    }
    if (!this._views.has(id)){
      log(`getOrCreateView: creating new view: ${id}`)
      const view$ = new ReplaySubject<View>(1);
      return await this.buildView(table, config, id, view$);
    }else{
      log(`updateDataView: dropping and re-creating view: ${id}`, config)
      const viewMeta = this._views.get(id)!;
      const view$ = viewMeta.view
      const oldView = view$.currentValue();
      if (oldView)
      await oldView.delete();

      return await this.buildView(table, config, id, view$);
    }
  }
  getViewOrTable(id: string): IObservable<View> {
    log(`getViewOrTable: ${id}`)
    const mapper = (data: Array<TableMeta|ViewMeta>): Promise<View>|View|Symbol => {
      // log(`getViewOrTable mapper - potential change: observing for ${id}`)
      for (const ds of data){
        if (ds.id === id){
          if ('view' in ds){
            log(`getViewOrTable: found view ${id}`)
            const v = ds.view.currentValue()
            if (v)
              return v;
          }else {
            log(`getViewOrTable: found table ${id}`)
            return ds.table.view();
          }
        }
      }
      return map.DONT_TRIGGER_UPATE;
    };
    return map(this.dataChanged, mapper, true) as IObservable<View>;
  }
  getDependentTable = async (id: string, sourceTable: string, triggerViewName:string|ReplaySubject<View>) => {
    try {
      let depTableMeta: any|undefined;
      const triggerView = triggerViewName instanceof ReplaySubject<View>
        ? triggerViewName
        : await pdqDataBroker.getOrCreateView(triggerViewName, sourceTable)


      log(`getDependentTable: subscribing to dependent table ${triggerViewName}...`)
      triggerView.subscribe(async i => {                                                                                                // todo - track and unsubscribe
        if (!depTableMeta) {
          log(`getDependentTable: creating dependent table ${triggerViewName} changed s1... ${sourceTable}`)

          const schema = await i.schema()
          depTableMeta = {
            id,
            table: await this._worker.table(schema),
            references: new Set([sourceTable]),
            parents: new Set([sourceTable]),
          };

          await depTableMeta.table.replace(await i.to_arrow());
          this._setTable(id, depTableMeta)
          return
        }
        log(`getDependentTable: updating dependent table ${triggerViewName} changed s2... ${sourceTable}`)
        const arrow = await i.to_arrow()
        await depTableMeta.table.replace(arrow);
        log(`getDependentTable: updated complete`);
      });
    }catch(e){
      log(`getDependentTable: error ${e}`)
      console.error(e)
    }
  }
  getTableMeta(id: string): Promise<TableMeta|undefined> {
    return this._getTable(id);
  }
  getViewNames() {
    return [...this._views.keys(), ...this._tables.keys()];
  }
  private _dataChangedHook()  {
    log('data change hook')
    this.dataChanged.next([...this._tables.values(), ...this._views.values()]);
  }
  private async buildView(table: string, config: any, id: string, view$: ReplaySubject<View>) {
    const t = this._tables.get(table)!;
    const v = await this._tables.get(table)!.table.view(config);
    this._views.set(id, {
      id,
      table: t.table,
      view: view$,
      options: config,
      references: new Set([table]),
      parents: new Set([table]),
      dependents: new Set([])
    });
    t.references.add(id);
    t.dependents.add(id);
    view$.next(v);
    await pdqDataBroker.getDependentTable(`${id}-result-table`, table, view$);
    this._dataChangedHook()
    return view$;
  }
  private tableResolvers = new Map<string, Delayed<TableMeta>>;
  private _setTable(id: string, table: TableMeta): void{
    log('setTable', id, table)
    this._tables.set(id,table);
    if (this.tableResolvers.has(id)){
      this.tableResolvers.get(id)!.resolve(table);
    }
    this._dataChangedHook()
  }
  private _getTable(id: string): Promise<TableMeta>{
    log('getTable', id)
    if (this._tables.has(id)){
      return Promise.resolve(this._tables.get(id)!);
    }
    if (this.tableResolvers.has(id)){
      return this.tableResolvers.get(id)!.promise;
    }
    let resolve: (t:TableMeta) => void ;
    const promise = new Promise<TableMeta>((r, _) => {
      log('getTable resolving', id);
      resolve = r
    });
    if (resolve!)
      this.tableResolvers.set(id, {resolve, promise});
    return promise;
  }
}

export const pdqDataBroker = new PdqDataBroker();

export default pdqDataBroker;


type Delayed<T> = {
  resolve: (t:T) => void,
  promise: Promise<T>
}
